/**
 * The overlapping team discs on a fixture row (design-system.md §6 "Fixture
 * row" → Crests; `.m-crest` / `.crest` in the prototype).
 *
 * A placeholder treatment, not final artwork: two letters of the team name on
 * the team's colour. §6's note says to swap in real crest images when they exist
 * and keep the 32×32 circle with its 2px white ring — which is what the ring is
 * for, separating the discs where they overlap.
 *
 * Decorative throughout. The fixture row's title already says "Kolding vs
 * Hillerod", so announcing "KO HI" as well would only repeat it worse.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

export interface CrestTeam {
  name: string;
  /** The team's own colour, from the feed — data, not a design token. */
  color: string;
}

interface CrestsProps {
  home: CrestTeam;
  /** Omitted for a tournament fixture, which has no opposing side. */
  away?: CrestTeam;
}

const abbreviate = (name: string) => name.slice(0, 2).toUpperCase();

const Crest = ({ team, overlap }: { team: CrestTeam; overlap?: boolean }) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'grid',
      placeItems: 'center',
      width: '32px',
      height: '32px',
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: team.color,
      border: `2px solid ${theme.tokens.color.surface}`,
      color: theme.tokens.color.surface,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.03em',
      ...(overlap ? { marginLeft: '-10px' } : {}),
    })}
  >
    {abbreviate(team.name)}
  </Box>
);

export const Crests = ({ home, away }: CrestsProps) => (
  <Box aria-hidden component="span" sx={{ display: 'flex', alignItems: 'center' }}>
    <Crest team={home} />
    {away ? <Crest team={away} overlap /> : null}
  </Box>
);
